import { FunctionComponent, useCallback, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { styles } from '../../../../styles/themes/style'

import ProfilePhoto from '../../../../components/ProfilePhoto'
import LoaderCell from '../../../../components/LoaderCell'
import SvgFragment from '../../../../fragments/SvgFragment'
import Row from '../../../../components/Row'
import Column from '../../../../components/Column'
import Text from '../../../../components/Text'
import TooltipWrapper from '../../../../components/TooltipWrapper'

import { IConnection } from '../../../../interfaces/models/connection'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { SidebarTypes } from '../../../../interfaces/store/appStore'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers'
import { isMobileDevice } from '../../../../utils'

import HomeHighlightsSyncPrompt from './HomeHighlightsSyncPrompt'
import HomeHighlightsSyncingPrompt from './HomeHighlightsSyncingPrompt'
import HomeHighlightsMobileDesktopPrompt from './HomeHighlightsMobileDesktopPrompt'
import { BIRTHDAY_PREVIEW_CONNECTIONS } from './homeHighlightsPreviewData'

interface IHomeBirthdateTableProps {
    birthdateConnections: any;
    birthdateConnectionsLoading: any
    isSynced?: boolean
    syncInProgress?: boolean
    extensionInstalled?: boolean
    onSyncContacts?: () => void
}

interface IListRowProps {
    $isLast?: boolean
    $faded?: boolean
}

const HomeBirthdateTable: FunctionComponent<IHomeBirthdateTableProps> = ({
    birthdateConnections,
    birthdateConnectionsLoading,
    isSynced = true,
    syncInProgress = false,
    extensionInstalled = true,
    onSyncContacts,
}) => {
    const { openSidebar } = useAppHandlers()
    const { messageClickHandler, getConnectionByPublicIdentifierHandler } = useConnectionsHandlers()
    const { store: { workspace } } = useAuthStore()
    const [loadingConversation, setLoadingConversation] = useState<string | null>(null)

    const formatDate = (dateObj: { year?: number; month?: number; day?: number }) => {
        const { year, month, day } = dateObj

        if(month !== undefined && day !== undefined) {
            const date = moment({ year: year ?? 2000, month: month - 1, day })
            return date.isValid()
                ? year !== undefined
                    ? date.format("MMMM D, YYYY")
                    : date.format("MMMM D,")
                : null
        }

        return null
    }

    const handleOpenConnectionSidebar = useCallback(async (connection: IConnection) => {
        try {
            const publicIdentifier = connection.publicIdentifier
            if (publicIdentifier) {
                const result = await getConnectionByPublicIdentifierHandler(publicIdentifier)
                const fullConnection = result?.data || result

                if (fullConnection?._id) {
                    openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection: fullConnection })
                    return
                }
            }
        } catch (error) {
            console.error('Failed to load full connection for sidebar', error)
        }

        openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection })
    }, [getConnectionByPublicIdentifierHandler, openSidebar])

    const renderBirthdayRow = (connection: IConnection, index: number, total: number, faded = false) => {
        const isPreview = !!(connection as any)._isPreview

        return (
        <BirthdayGroup key={connection._id} $isLast={index === total - 1} $faded={faded}>
            <ListRow>
                <ProfilePhoto
                    source={connection.profilePicture}
                    width="40px"
                    height="40px"
                    minWidth="40px"
                    minHeight="40px"
                    capitalLetters={`${connection?.firstName?.charAt(0) || ''}${connection?.lastName?.charAt(0) || ''}`}
                />
                <PersonInfo>
                    <PersonName
                        type="button"
                        disabled={faded || isPreview}
                        onClick={() => !faded && !isPreview && handleOpenConnectionSidebar(connection)}
                    >
                        {`${connection?.firstName} ${connection?.lastName}`}
                    </PersonName>
                    <DateRow alignItems gap="6px">
                        <CalendarIcon aria-hidden>
                            <SvgFragment type="calendar14" />
                        </CalendarIcon>
                        <BirthDate>{formatDate(connection.birthDate)}</BirthDate>
                    </DateRow>
                </PersonInfo>
                <CtaColumn>
                    <TooltipWrapper
                        tooltipText={
                            workspace?.isStarter
                                ? 'Upgrade to Pro or Business to unlock this feature'
                                : ''
                        }
                    >
                        <BirthdateCta
                            type="button"
                            disabled={
                                faded ||
                                isPreview ||
                                workspace?.isStarter ||
                                loadingConversation === connection._id
                            }
                            onClick={(e) => {
                                if (faded || isPreview || workspace?.isStarter) return
                                messageClickHandler(
                                    e,
                                    connection,
                                    setLoadingConversation,
                                    `Happy Birthday ${connection?.firstName}! I wish you all the best on your special day!`
                                )
                            }}
                        >
                            {loadingConversation === connection._id
                                ? 'Loading conversation...'
                                : 'Say happy birthday'}
                        </BirthdateCta>
                    </TooltipWrapper>
                </CtaColumn>
            </ListRow>
        </BirthdayGroup>
    )}

    const previewConnections = !isSynced ? BIRTHDAY_PREVIEW_CONNECTIONS : birthdateConnections
    const isMobile = isMobileDevice()

    return (
        <ListScroll>
            {birthdateConnectionsLoading ? (
                Array(4).fill(undefined).map((_, rowIndex) => (
                    <BirthdaySkeletonRow key={rowIndex} $isLast={rowIndex === 3}>
                        <LoaderCell $circle $width="40px" $height="40px" />
                        <SkeletonPersonInfo>
                            <LoaderCell $width="72%" $height="14px" />
                            <LoaderCell $width="48%" $height="12px" />
                        </SkeletonPersonInfo>
                        <SkeletonCta>
                            <LoaderCell $width="132px" $height="28px" />
                        </SkeletonCta>
                    </BirthdaySkeletonRow>
                ))
            ) : !isSynced && isMobile ? (
                <HomeHighlightsMobileDesktopPrompt />
            ) : !isSynced && syncInProgress ? (
                <BirthdayList>
                    {previewConnections.map((connection: IConnection, index: number) =>
                        renderBirthdayRow(connection, index, previewConnections.length, index > 0)
                    )}
                    <HomeHighlightsSyncingPrompt />
                </BirthdayList>
            ) : !isSynced ? (
                <BirthdayList>
                    {previewConnections.map((connection: IConnection, index: number) =>
                        renderBirthdayRow(connection, index, previewConnections.length, index > 0)
                    )}
                    <HomeHighlightsSyncPrompt
                        title="Sync your contacts to see upcoming birthdays"
                        subtitle="Never miss a reason to reconnect."
                        extensionInstalled={extensionInstalled}
                        onSyncContacts={onSyncContacts ?? (() => {})}
                    />
                </BirthdayList>
            ) : birthdateConnections.length ? (
                <BirthdayList>
                    {birthdateConnections.map((connection: IConnection, index: number) =>
                        renderBirthdayRow(connection, index, birthdateConnections.length)
                    )}
                </BirthdayList>
            ) : (
                <EmptyPageWrapper>
                    <SvgFragment type="templateEmptyBlue" />
                    <Text $heading4>{'No Birthdays Today'}</Text>
                </EmptyPageWrapper>
            )}
        </ListScroll>
    )
}

const divider = `1px solid ${styles.colors.black200}`

const ListScroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`

const BirthdayList = styled(Column)`
    width: 100%;
    gap: 4px;
`

const BirthdayGroup = styled.div<IListRowProps>`
    width: 100%;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : divider)};
    ${({ $faded }) => $faded && `
        opacity: 0.35;
        pointer-events: none;
        user-select: none;
    `}
`

const ListRow = styled(Row)`
    width: 100%;
    padding: 10px 0;
    gap: 20px;
    align-items: flex-start;
    background: ${styles.colors.white};
    border-radius: 4px;
`

const PersonInfo = styled(Column)`
    flex: 1;
    min-width: 0;
    justify-content: center;
`

const PersonName = styled.button`
    padding: 0;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    color: ${styles.colors.black600};

    &:hover {
        color: ${styles.colors.primary600};
    }

    &:disabled {
        cursor: default;
    }
`

const DateRow = styled(Row)`
    display: flex;
    align-items: flex-start;
`

const CalendarIcon = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    align-items: center;
    justify-content: center;
    line-height: 0;

    & .svg {
        width: 14px;
        height: 14px;
    }

    & .path {
        stroke: ${styles.colors.black400} !important;
    }
`

const BirthDate = styled.span`
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    color: ${styles.colors.black400};
`

const CtaColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    margin-left: auto;
`

const BirthdateCta = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    min-height: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${styles.colors.black200};
    background: ${styles.colors.white};
    color: ${styles.colors.black500};
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
        background: ${styles.colors.black100};
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`

const BirthdaySkeletonRow = styled(Row)<IListRowProps>`
    width: 100%;
    padding: 10px 0;
    gap: 20px;
    align-items: flex-start;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : divider)};
`

const SkeletonPersonInfo = styled(Column)`
    flex: 1;
    min-width: 0;
    gap: 6px;
`

const SkeletonCta = styled.div`
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
`

const EmptyPageWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 24px 0;
    text-align: center;

    & svg {
        width: 42px;
        height: 42px;
    }
`

export default HomeBirthdateTable
